import React, { ComponentProps, useCallback, useContext, useMemo, useState } from 'react';
import Workspace from '../../components/Workspace/Workspace';
import { ProductsStockTransaction } from '../../graphql/entities';
import StockTransactionModal, { supportedDocumentTypes } from '../../components/modals/StockTransactionModal';
import Formatters from '../../components/grid-addons/formatters';
import { ALLOW_ADD, ALLOW_VIEW } from '../../utils/OperationPermissions';
import fieldMetaData, { IEnumMetaData } from '../../graphql/schema/fieldMetaData';
import getProductDP from '../../utils/getProductDP';
import attributeQuery from '../../graphql/queries/product/LotAttributeConfigListQuery.graphql';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import { useLotAttributesForWorkspace } from '../../components/Workspace/useAttributesForWorkspace';
import {
  CostViewPermission,
  Direction,
  DocumentType,
  IGenerateBondedStockReportMutationResult,
  IGenerateBondedStockReportMutationVariables,
  IStockTransaction,
} from '../../graphql/schema/p4types';
import { Paths } from '../../@types/keyOfNested';
import AppContext from '../../components/Context/AppContext';
import { IFormatterProps, IWorkspaceAction, IWorkspaceColumnOverride } from '../../@types/workspaceTypes';
import FormModalFormatter from '../../components/modals/formatters/FormModalFormatter';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import { useFullUserDetails, usePrecision } from '../../utils/hooks';
import { WorkspaceActionButtons } from '../../components/Workspace/WorkspaceActionButtons';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import GenerateBondedStockReport from '../../graphql/mutations/product/GenerateBondedStockReport.graphql';
import getClientMutationId from '../../utils/getClientMutationId';
import { useActionCriteria } from '../../utils/useActionProps';

const PRODUCTS_STOCK_LOT_TRANSACTION_FIELD_NAMES: Array<Paths<IStockTransaction>> = [
  'createdAt',
  'documentType',
  'documentNumber',
  'product.reference',
  'product.description',
  'binLocation.warehouse.description',
  'binLocation.location',
  'quantityChange',
  'createdBy',
  'stockTransactionReason',
];

// Helper function to trigger file download
const downloadFile = (url: string, filename?: string) => {
  const link = document.createElement('a');
  link.href = url;

  // The 'download' attribute suggests a filename to the browser
  // and forces download behavior instead of navigation.
  // If no filename is provided, the browser might use the last part of the URL
  // or the filename from the Content-Disposition header.
  if (filename) {
    link.setAttribute('download', filename);
  } else {
    // Still set the attribute to encourage download behavior
    link.setAttribute('download', '');
  }

  // Append to the document, click, and remove
  // Some browsers require the link to be in the document body
  link.style.display = 'none'; // Keep it hidden
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const ProductStockTransactionActions = (
  props: Omit<ComponentProps<typeof WorkspaceActionButtons>, 'actionButtons'>
) => {
  const fullUserDetails = useFullUserDetails();
  if (!fullUserDetails?.flags?.bondedStockReport) {
    return <WorkspaceActionButtons {...props} actionButtons={[]} />;
  }

  const actionCriteria = useActionCriteria();

  const [doGenerateBondedStockReport] = useBackgroundMutation<
    IGenerateBondedStockReportMutationResult,
    IGenerateBondedStockReportMutationVariables
  >(GenerateBondedStockReport, {
    onCompleteJob: (jobInfo) => {
      if (jobInfo.properties?.rfunnel) {
        const rfunnel = jobInfo.properties.rfunnel;
        const downloadUrl = `/api/resources/temporary/${rfunnel}`;
        // Provide rfunnel as the suggested filename
        downloadFile(downloadUrl, rfunnel);
      }
      if (jobInfo.properties?.fcstock) {
        const fcstock = jobInfo.properties.fcstock;
        const downloadUrl = `/api/resources/temporary/${fcstock}`;
        // Provide fcstock as the suggested filename
        downloadFile(downloadUrl, fcstock);
      }
    },
  });

  const generateBondedStockReportAction: IWorkspaceAction = useMemo(
    () => ({
      key: 'GENERATE_BONDED_STOCK_REPORT',
      text: 'Bonded Stock Report',
      errorMessage: 'Could not generate bonded stock report',
      actionReference: 'GENERATE_BONDED_STOCK_REPORT',
      onClick: () =>
        doGenerateBondedStockReport({
          variables: {
            input: {
              clientMutationId: getClientMutationId(),
              actionCriteria,
            },
          },
        }),
    }),
    [doGenerateBondedStockReport]
  );

  return <WorkspaceActionButtons {...props} actionButtons={[generateBondedStockReportAction]} />;
};

const ProductsStockTransactionWorkspace = (props) => {
  const appContext = useContext(AppContext);

  const lotAttributeColumns = useLotAttributesForWorkspace(attributeQuery, {
    valuesPath: 'stockLot.lotAttributeValues',
    columnKey: 'stockLot.lotAttributeValues.attributeConfig.title',
  });

  const costPricePrecision = usePrecision('cost');
  const columnOverrides: IWorkspaceColumnOverride = useMemo(
    () => ({
      'product.description': {
        name: 'Description',
      },
      'binLocation.location': {
        name: 'Bin Location',
      },
      'binLocation.warehouse.description': {
        name: 'Warehouse',
      },
      'product.costUnitDescription': {
        name: 'Product - Unit Cost Description',
      },
      'product.costUnitQuantity': {
        name: 'Product - Unit Cost Quantity',
      },
      costUnitDescription: {
        name: 'Transaction - Unit Cost Description',
      },
      costUnitQuantity: {
        name: 'Transaction - Unit Cost Quantity',
      },
      costPrice: {
        name: 'Cost Price',
        formatter: 'numeric',
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      previousValuationCost: {
        name: 'Previous Valuation Cost',
        formatter: 'numeric',
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      adjustedCostPrice: {
        name: 'Adjusted Cost Price',
        formatter: 'numeric',
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      currentValuationCost: {
        name: 'Current Valuation Cost',
        formatter: 'numeric',
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      singleUnitCost: {
        name: 'Single Unit Cost',
        formatter: 'numeric',
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      quantityChange: {
        name: 'Quantity',
        formatter: 'numeric',
        options: {
          format: { decimals: props.precision ?? getProductDP },
        },
      },
      quantityToReconcile: {
        name: 'Quantity To Reconcile',
        formatter: 'numeric',
        options: {
          format: { decimals: props.precision ?? getProductDP },
        },
      },
      documentType: {
        name: 'Source Type',
        key: 'documentType',
        width: 140,
        options: {
          enumValues: (fieldMetaData.DocumentType as IEnumMetaData).enumValues,
        },
        formatter: Formatters.EnumFormatter,
      },
      documentNumber: {
        name: 'Source No',
        formatter: StockTransactionModalFormatter,
        width: 125,
      },
      createdAt: {
        name: 'Date',
        formatter: 'dateTime',
        width: 140,
      },
    }),
    [costPricePrecision, props.precision, getProductDP]
  );

  const availableColumnsFilter = useCallback(
    (column: IEditableDataGridColumnDef): boolean => {
      if (!column?.fieldName) {
        return false;
      }
      return !(
        appContext?.fullUserDetails?.companyUser?.costViewPermission !== CostViewPermission.All &&
        (column.fieldName === 'costPrice' ||
          column.fieldName === 'adjustedCostPrice' ||
          column.fieldName === 'currentValuationCost' ||
          column.fieldName === 'previousValuationCost' ||
          column.fieldName === 'previousStockValuation' ||
          column.fieldName === 'currentStockValuation')
      );
    },
    [appContext?.fullUserDetails?.companyUser?.costViewPermission]
  );

  const workspaceProps = useWorkspace<IStockTransaction>(props, ProductsStockTransaction, {
    columns: PRODUCTS_STOCK_LOT_TRANSACTION_FIELD_NAMES,
    customColumns: lotAttributeColumns,
    columnOverrides: columnOverrides,
    initialSortColumn: 'createdAt',
    initialSortDirection: Direction.Desc,
  });

  return (
    <Workspace
      entityType={ProductsStockTransaction.type}
      title={ProductsStockTransaction.title}
      defaultQuery={ProductsStockTransaction.workspace}
      permittedOperations={ALLOW_VIEW}
      availableColumnsFilter={availableColumnsFilter}
      {...workspaceProps}
      {...props}
      entity={ProductsStockTransaction}
      workspaceActionButtonsHOC={ProductStockTransactionActions}
      noEdit
    />
  );
};

const StockTransactionModalFormatter = (props: IFormatterProps<IStockTransaction>) => {
  const [modalOpen, setModalOpen] = useState(false);

  const sourceId = props.dependentValues.rowData.sourceId;
  const documentType = props.dependentValues.rowData.documentType;
  const documentNumber = props.dependentValues.rowData.documentNumber;

  const alreadyInOrderForm = /.*.*Sales\/Order\/Details.*|.*Quote\/Details.*/.test(window.location.pathname);

  const onClick = useMemo(() => {
    if (alreadyInOrderForm && documentType === DocumentType.Allocation) {
      return () => {
        window.open(`${window.location.origin}/Sales/Order/Details/${documentNumber}`, '_blank');
      };
    }
    return undefined;
  }, [alreadyInOrderForm, documentNumber, documentType]);

  return (
    <FormModalFormatter
      modalOpen={modalOpen}
      setModalOpen={setModalOpen}
      value={sourceId}
      onClick={onClick}
      hasModalTarget={supportedDocumentTypes.includes(documentType)}
    >
      <StockTransactionModal
        forceReadOnly={true}
        rowData={props.dependentValues.rowData}
        match={{ params: { id: sourceId } }}
        overrideParams={{ id: sourceId }}
        onClose={() => {
          setModalOpen(false);
        }}
        modalClose={true}
      />
    </FormModalFormatter>
  );
};

export default ProductsStockTransactionWorkspace;
